import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../sessionSlice";
import { fetchThemeData } from "../../themeSlice";
import "./quizSettings.css";
import { updateThemeData } from "../../functions/updateThemeData";
import WmlAvatarSettings from "../wmlAvatarSettings/WmlAvatarSettings";

export default function QuizSettings({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [winnerPoint, setWinnerPoint] = useState("1");
  const [majorityPoints, setMajorityPoints] = useState("0");
  const [wmlQuestionTimerSeconds, setWmlQuestionTimerSeconds] = useState("0");

  useEffect(() => {
    if (!isOpen || !data) return;

    const wp = parseInt(data.winner_point ?? data.correctPoints, 10);
    setWinnerPoint(String(Number.isNaN(wp) ? 1 : Math.max(0, wp)));
    const mp = parseInt(data.majority_points ?? data.wrongPoints ?? 0, 10);
    setMajorityPoints(String(Number.isNaN(mp) ? 0 : Math.max(0, mp)));
    const t = parseInt(data.wmlQuestionTimerSeconds, 10);
    setWmlQuestionTimerSeconds(String(Number.isNaN(t) ? 0 : Math.max(0, t)));
  }, [isOpen, data, currentTheme]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  const handleSubmit = async () => {
    const wp = parseInt(winnerPoint, 10);
    const winnerPointToSend = Number.isNaN(wp) ? 1 : Math.max(0, wp);
    const mp = parseInt(majorityPoints, 10);
    const majorityPointsToSend = Number.isNaN(mp) ? 0 : Math.max(0, mp);
    const ts = parseInt(wmlQuestionTimerSeconds, 10);
    const timerSec = Number.isNaN(ts) ? 0 : Math.max(0, ts);
    const payload = {
      data: {
        winner_point: winnerPointToSend,
        majority_points: majorityPointsToSend,
        wmlQuestionTimerSeconds: String(timerSec),
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      Swal.fire("Success", "Settings saved!", "success");
      window.location.reload();
      onClose();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save settings", "error");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="quiz-settings-modal">
        <h2>Settings</h2>

        <div className="setting-row">
          <div className="setting-input">
            <label>Winner points</label>
            <input
              type="number"
              min="0"
              value={winnerPoint}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  setWinnerPoint("");
                  return;
                }
                const n = parseInt(raw, 10);
                if (Number.isNaN(n)) return;
                setWinnerPoint(String(Math.max(0, n)));
              }}
              onBlur={() => {
                if (winnerPoint === "") setWinnerPoint("1");
              }}
            />
            <div className="code">WML: points awarded to the voted winner player.</div>
          </div>
          <div className="setting-input">
            <label>Majority points</label>
            <input
              type="number"
              min="0"
              value={majorityPoints}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  setMajorityPoints("");
                  return;
                }
                const n = parseInt(raw, 10);
                if (Number.isNaN(n)) return;
                setMajorityPoints(String(Math.max(0, n)));
              }}
              onBlur={() => {
                if (majorityPoints === "") setMajorityPoints("0");
              }}
            />
            <div className="code">WML: points awarded to each voter in the majority.</div>
          </div>
          <div className="setting-input">
            <label>Question timer (seconds)</label>
            <input
              type="number"
              min="0"
              value={wmlQuestionTimerSeconds}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  setWmlQuestionTimerSeconds("");
                  return;
                }
                const x = parseInt(raw, 10);
                if (Number.isNaN(x)) return;
                setWmlQuestionTimerSeconds(String(Math.max(0, x)));
              }}
              onBlur={() => {
                if (wmlQuestionTimerSeconds === "") setWmlQuestionTimerSeconds("0");
              }}
            />
            <div className="code">0 = off. Countdown per question; auto show result at 0.</div>
          </div>
        </div>

        <div className="quiz-settings-modal__wml-avatars">
          <h3 className="quiz-settings-modal__wml-title">Characters</h3>
          <WmlAvatarSettings embedded />
        </div>

        <div className="actions">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="save-btn" onClick={handleSubmit}>
            Save
          </button>
        </div>
      </div>
    </>
  );
}
